import React, { useState, useEffect, useCallback } from 'react';
import { investmentPerspectivesAPI } from '../../services/api';

const FIRST_PERIOD = '2026-07';

/**
 * Manage which client domains are white-labelled, PER MONTH: ticked domains get
 * their own primary colour on navy/brand-token shapes and their own logo on
 * logo blocks for that month's cover; everyone else renders Drummond navy and
 * the Drummond logo. Membership is a curated business decision (no automatic
 * rule). Ticks are local until Save is pressed ("Saved" confirms); Copy From
 * replaces the selected month's list with another month's.
 */
const WhitelabelModal = ({ isOpen, onClose, isDrummondUser, brands, period }) => {
  const [months, setMonths] = useState([]);
  const [selected, setSelected] = useState(period || '');
  const [whitelabel, setWhitelabel] = useState(new Set()); // local (unsaved) state
  const [baseline, setBaseline] = useState(new Set());     // last saved state
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState('idle');      // idle | saving | saved | error
  const [copySource, setCopySource] = useState('');
  const [copying, setCopying] = useState(false);

  const loadMonth = useCallback(async (p) => {
    if (!p) return;
    setLoading(true);
    setSaveState('idle');
    try {
      const res = await investmentPerspectivesAPI.getWhitelabelDomains(p);
      const s = new Set(res.domains || []);
      setWhitelabel(new Set(s));
      setBaseline(s);
    } catch (err) {
      console.error('Error loading white-label list:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || !isDrummondUser) return;
    const p = period || '';
    setSelected(p);
    setCopySource('');
    investmentPerspectivesAPI.getAvailableMonths()
      .then((res) => {
        const ms = (res.months || [])
          .filter((m) => m.value >= FIRST_PERIOD)
          .sort((a, b) => (a.value < b.value ? 1 : -1)); // latest first
        setMonths(ms);
      })
      .catch((err) => console.error('Error loading months:', err));
    loadMonth(p);
  }, [isOpen, isDrummondUser, period, loadMonth]);

  const changeMonth = (p) => {
    setSelected(p);
    setCopySource('');
    loadMonth(p);
  };

  const toggle = (domain) => {
    setWhitelabel((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain); else next.add(domain);
      return next;
    });
    setSaveState('idle');
  };

  const dirty = whitelabel.size !== baseline.size
    || [...whitelabel].some((d) => !baseline.has(d));

  const handleSave = async () => {
    const adds = [...whitelabel].filter((d) => !baseline.has(d));
    const removes = [...baseline].filter((d) => !whitelabel.has(d));
    setSaveState('saving');
    try {
      if (adds.length) await investmentPerspectivesAPI.updateWhitelabelDomains(selected, adds, true);
      if (removes.length) await investmentPerspectivesAPI.updateWhitelabelDomains(selected, removes, false);
      setBaseline(new Set(whitelabel));
      setSaveState('saved');
    } catch (err) {
      console.error('Error saving white-label list:', err);
      setSaveState('error');
    }
  };

  const handleCopy = async () => {
    if (!copySource || copySource === selected) return;
    setCopying(true);
    try {
      const res = await investmentPerspectivesAPI.copyWhitelabelDomains(selected, copySource);
      const s = new Set(res.domains || []);
      setWhitelabel(new Set(s));
      setBaseline(s);
      setSaveState('saved');
    } catch (err) {
      console.error('Error copying white-label list:', err);
      setSaveState('error');
    } finally {
      setCopying(false);
    }
  };

  if (!isOpen) return null;

  // Only list domains with a genuine brand colour — a primary that isn't the
  // Drummond navy default. Domains already on this month's list stay visible
  // regardless, so they can always be unticked.
  const NAVY_DEFAULTS = ['#072040', '#012341'];
  const hasBrandColour = (b) =>
    b.primary_color && !NAVY_DEFAULTS.includes(b.primary_color.toLowerCase());
  const byName = (a, b) => {
    const an = (a.company_name || a.domain).toLowerCase();
    const bn = (b.company_name || b.domain).toLowerCase();
    return an < bn ? -1 : 1;
  };
  const visible = (brands || [])
    .filter((b) => hasBrandColour(b) || baseline.has(b.domain) || whitelabel.has(b.domain));
  const tailoredRows = visible.filter((b) => b.tailored).sort(byName);
  const otherRows = visible.filter((b) => !b.tailored).sort(byName);

  const saveLabel = saveState === 'saving' ? 'Saving…'
    : saveState === 'saved' ? 'Saved ✓'
    : saveState === 'error' ? 'Retry Save'
    : 'Save';
  const monthLabel = (v) => months.find((m) => m.value === v)?.label || v;

  return (
    <div className="propagate-modal-overlay" onClick={onClose}>
      <div className="propagate-modal" onClick={(e) => e.stopPropagation()}>
        <div className="propagate-modal-header">
          <h3>Manage White-Label</h3>
          <button className="propagate-modal-close" onClick={onClose}>&times;</button>
        </div>

        <p className="propagate-modal-desc">
          Ticked domains get their own primary colour and logo on the cover for
          the selected month. Unticked domains render the Drummond navy and logo.
          Only domains with a brand colour in their palette are listed — remember
          to press Save.
        </p>

        <div className="ip-wl-monthbar">
          <label className="modal-field-label">
            Month
            <select
              className="ip-wl-month-select"
              value={selected}
              onChange={(e) => changeMonth(e.target.value)}
            >
              {months.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </label>
          <label className="modal-field-label">
            Copy from
            <select
              className="ip-wl-month-select"
              value={copySource}
              onChange={(e) => setCopySource(e.target.value)}
            >
              <option value="">Select month…</option>
              {months.filter((m) => m.value !== selected).map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </label>
          <button
            className="ip-publish-row-btn"
            onClick={handleCopy}
            disabled={!copySource || copying || loading}
            title={copySource
              ? `Replace ${monthLabel(selected)}'s list with ${monthLabel(copySource)}'s`
              : 'Pick a month to copy from'}
          >
            {copying ? 'Copying…' : 'Copy'}
          </button>
        </div>

        <div className="propagate-grid-wrapper">
          {loading ? (
            <div className="publish-loading">Loading domains…</div>
          ) : (
            <table className="propagate-grid">
              <thead>
                <tr>
                  <th className="propagate-corner">Client</th>
                  <th className="propagate-col-header">Domain</th>
                  <th className="propagate-col-header">Colour</th>
                  <th className="propagate-col-header">White-label</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { title: 'Tailored', rows: tailoredRows },
                  { title: 'Non-Tailored', rows: otherRows },
                ].map((group) => group.rows.length > 0 && (
                  <React.Fragment key={group.title}>
                    <tr className="propagate-page-header">
                      <td colSpan={4}>{group.title}</td>
                    </tr>
                    {group.rows.map((b) => (
                      <tr key={b.domain}>
                        <td className="propagate-row-label">{b.company_name || b.domain}</td>
                        <td className="ip-wl-domain">{b.domain}</td>
                        <td className="propagate-cell">
                          <span
                            className="ip-wl-swatch"
                            style={{ background: b.primary_color || '#072040' }}
                            title={b.primary_color || ''}
                          />
                        </td>
                        <td className="propagate-cell">
                          <input
                            type="checkbox"
                            checked={whitelabel.has(b.domain)}
                            disabled={saveState === 'saving'}
                            onChange={() => toggle(b.domain)}
                          />
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="propagate-modal-footer">
          {saveState === 'error' && (
            <span className="ip-wl-error">Save failed — try again</span>
          )}
          <button className="btn-cancel" onClick={onClose}>Close</button>
          <button
            className="btn-publish"
            onClick={handleSave}
            disabled={loading || saveState === 'saving' || (!dirty && saveState !== 'error')}
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhitelabelModal;
